import { anyOf, toString as queryfilterToString } from '@gonfalon/queryfilter/builder';
import { getContextsKindKeysQuery, getContextsWithNamesQuery } from '@gonfalon/rest-api/queries/contexts';
import { useQuery } from '@tanstack/react-query';

import type { LDContextCommon } from '../types/types';

/**
 * Fetches context data for a given context kind and key.
 * Makes an initial query to get the context, then a follow-up query to get the name if not present.
 */
export function useContextData({
  projectKey,
  environmentKey,
  contextKind,
  contextKey,
  enabled = true,
}: {
  projectKey: string;
  environmentKey: string;
  contextKind: string;
  contextKey: string;
  enabled?: boolean;
}) {
  const {
    data: initialData,
    isPending,
    isError,
    refetch,
  } = useQuery({
    ...getContextsKindKeysQuery({
      projectKey,
      environmentKey,
      query: {
        filter: queryfilterToString(anyOf('kindkey', [`${contextKind}:${contextKey}`])),
        sort: '-ts',
        limit: 50,
      },
    }),
    select: (result) => {
      const contextData = result.items.map((item) => ({
        context: item.context as LDContextCommon,
      }));
      const found = contextData?.find(
        (item) => item.context.kind === contextKind && item.context.key === contextKey,
      )?.context;
      return found;
    },
    enabled: enabled && Boolean(contextKey && contextKind),
  });

  // If we have context data but no name, make a dependent query to fetch the context with name
  const { data: contextWithName } = useQuery({
    ...getContextsWithNamesQuery({
      projectKey,
      environmentKey,
      kindKeys: [`${contextKind}:${contextKey}`],
    }),
    select: (result) => {
      const contextData = result.items.map((item) => ({
        context: item.context as LDContextCommon,
      }));
      const found = contextData?.find(
        (item) => item.context.kind === contextKind && item.context.key === contextKey,
      )?.context;
      return found;
    },
    enabled: enabled && Boolean(contextKey && contextKind && initialData && !initialData.name),
  });

  // Merge the data, preferring the context with name if available
  const data = contextWithName || initialData;

  // `isPending`/`isError` reflect the initial kind-key resolve query (not the dependent name-fill),
  // so callers can distinguish "still loading" from "resolved but not found" (data === undefined).
  return { data, isPending, isError, refetch };
}
